/**
 * Client-stored "remembered organization" hint powering the sign-in screen's
 * organization card (approved Phibian sign-in design).
 *
 * Purely local + cosmetic — never sent to the server and never used for
 * authorization. It exists because the sign-in screen renders *before*
 * authentication (`me` is null there), so there is no server org context to
 * display. The shell writes the last-known org after successful auth; the
 * sign-in screen reads it back so it can show real (not hardcoded) context.
 *
 * Only non-sensitive display fields (org name + slug) are persisted — no
 * tokens, no email, no user data.
 */
const KEY = 'cwos.admin.rememberedOrg';

export interface RememberedOrg {
  name: string;
  slug?: string;
}

export function readRememberedOrg(): RememberedOrg | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<RememberedOrg>;
      if (typeof parsed.name === 'string' && parsed.name.trim()) {
        return { name: parsed.name.trim(), slug: parsed.slug ?? undefined };
      }
    }
  } catch {
    // storage unavailable or malformed — treat as absent
  }
  return null;
}

export function writeRememberedOrg(org: { name?: string | null; slug?: string | null }): void {
  if (!org.name) return;
  try {
    window.localStorage.setItem(
      KEY,
      JSON.stringify({ name: org.name, slug: org.slug ?? undefined }),
    );
  } catch {
    // storage unavailable — best-effort only
  }
}

export function clearRememberedOrg(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // storage unavailable — best-effort only
  }
}
